import APIHelper from "./APIHelper";
import { GetActiveCountryCookie, SetCrossSubdomainCookie } from "./Helper";
import { FeatureInfo, FeatureStatus, ProcessEnv, Rules } from "../types";

declare let process: ProcessEnv;

// Env Defaults
const appName = process.env.VUE_APP_CONTEXT || 'archistar';

class AuthUser {
  guid = undefined as undefined | string;
  id = undefined as undefined | number | string;
  firstName = undefined as undefined | string;
  lastName = undefined as undefined | string;
  email = undefined as undefined | string;
  mobile = undefined as undefined | string | number;
  lastLogin = undefined as undefined | Date | number;
  subscriptions = [] as any[];
  roles = [] as any[];
  features = [] as FeatureInfo[] | never[];
  companies = [] as any;
  authCountries = [] as string[];
  activeCountryCode = undefined as undefined | string;
  appAccess = [] as any;
  themeSettings = {} as any;
  authPromise = undefined as undefined | Promise<any>;

  isArchistarSubscriptionExpired(): boolean {
    if (this.appAccess.Archistar) {
      return (this.appAccess.Archistar === 'Expired');
    }
    return false;
  };
  isArchistarSubscriptionTrial(): boolean {
    if (this.appAccess.Archistar) {
      return (this.appAccess.Archistar === 'Trial');
    }
    return false;
  };
  isArchistarSubscriptionPending(): boolean {
    if (this.appAccess.Archistar) {
      return (this.appAccess.Archistar === 'Pending');
    }
    return false;
  };

  hasAccessToResource(resourceName: string): boolean {
    return this.features
      ?.filter(feature => feature.resource === resourceName)
      .map(feature => feature.hasAccess)
      .includes(true)
  };

  hasAccessToFeatureByName(featureName: string): boolean {
    return this.features
      ?.filter(feature => feature.featureName === featureName)
      .map(feature => feature.hasAccess)
      .includes(true)
  };

  isResourceHidden(resourceName: string): boolean {
    const statusList =  this.features
      ?.filter(feature => feature.resource === resourceName)
      .map(feature => feature.featureStatus);

    return (!statusList.includes(FeatureStatus.ENABLED) && statusList.includes(FeatureStatus.HIDDEN))
  };

  isFeatureHidden(featureName: string): boolean {
    const statusList =  this.features
    ?.filter(feature => feature.featureName === featureName)
      .map(feature => feature.featureStatus);
    
    return (!statusList.includes(FeatureStatus.ENABLED) && statusList.includes(FeatureStatus.HIDDEN))
  };
  
  getFeatureInfoToResource(resourceName: string): FeatureInfo[] | undefined {
    const resource = this.features.filter(feature => feature.resource === resourceName);
    return resource;
  };

  getFeatureInfoToFeatureByName(featureName: string): FeatureInfo[] | undefined {
    const resource = this.features.filter(feature => feature.featureName === featureName);
    return resource;
  };
  
  getFeatureStatusToResource(resourceName: string): boolean {
    return this.features
      ?.filter(feature => feature.resource === resourceName)
      .map(feature => feature.featureStatus == FeatureStatus.ENABLED)
      .includes(true)
  };

  getFeatureStatusToFeatureByName(featureName: string): FeatureStatus | string {
    const resource = this.features.find(feature => feature.featureName === featureName);
    if (resource) return resource.featureName;
    return FeatureStatus.UNKNOWN;
  };
  
  canReadResource(resourceName: string): boolean {
    const rules = new Set(
      this.features
        .filter(feature => feature.resource === resourceName && feature.hasAccess)
        .reduce((prev: Rules[], feature) => [...prev, ...feature.rule], [])
    );
    if (rules) return rules.has(Rules.Read);
    return false;
  };

  canReadFeatureByName(featureName: string): boolean {
    const rules = new Set(
      this.features
        .filter(feature => feature.featureName === featureName && feature.hasAccess)
        .reduce((prev: Rules[], feature) => [...prev, ...feature.rule], [])
    );
    if (rules) return rules.has(Rules.Read);
    return false;
  };
  
  canWriteResource(resourceName: string): boolean {
    const rules = new Set(
      this.features
        .filter(feature => feature.resource === resourceName && feature.hasAccess)
        .reduce((prev: Rules[], feature) => [...prev, ...feature.rule], [])
    );
    if (rules) return rules.has(Rules.Write);
    return false;
  };

  canResource(type: string, resourceName: string): boolean {
    const rules = new Set(
      this.features
        .filter(feature => feature.resource === resourceName && feature.hasAccess)
        .reduce((prev: string[], feature) => [...prev, ...feature.rule], [])
    );
    if (rules) return rules.has(type);
    return false;
  };

  canWriteFeatureByName(featureName: string): boolean {
    const rules = new Set(
      this.features
        .filter(feature => feature.featureName === featureName && feature.hasAccess)
        .reduce((prev: Rules[], feature) => [...prev, ...feature.rule], [])
    );
    if (rules) return rules.has(Rules.Write);
    return false;
  };

  exists(): boolean {
    return this.guid ? true : false;
  }

  fetch(to: any|undefined = undefined): Promise<any> {
    // Check State for a Promise in progress
    if (this.authPromise) {
      // Return existing promise to prevent duplicate API calls
      return this.authPromise;
    } else {
      // Promise Start
      const promise = new Promise(async (resolve, reject) => {
        try {
          const userInfo = (
            await APIHelper.AuthAPI.post(
              `/user/getappuserinfo?app_name=${appName}`
            )
          )?.data;
  
          // Update user info
          this.guid = userInfo.userData.guid;
          this.id = userInfo.userData.id;
          this.firstName = userInfo.userData.first_name;
          this.lastName = userInfo.userData.last_name;
          this.email = userInfo.userData.email;
          this.mobile = userInfo.userData.mobile;
          this.subscriptions = userInfo.userData.subscriptions;
          this.lastLogin = userInfo.userData.last_login;
          this.companies = userInfo.userData.companies;
          this.authCountries = userInfo.userData.authCountries;
          this.activeCountryCode = GetActiveCountryCookie();
          this.appAccess = userInfo.userData.app_access;

          // Update user themeSettings
          this.themeSettings = userInfo.themeSettings;
  
          // Update user roles
          this.roles = userInfo.userRoles;
  
          // Update user features
          this.features = userInfo.features;

          if (to && to.query.asCtryCode &&
            (!this.authCountries ||
              this.authCountries.length == 0 ||
              this.authCountries.includes(to.query.asCtryCode)
          )) {
            this.setActiveCountry(to.query.asCtryCode);
          }
          const countryCode = (to && ('clearAsCtryCode' in to.query) ? null : this.activeCountryCode);
          if (
            this.authCountries &&
            this.authCountries.length > 0 &&
            (!countryCode || !this.authCountries.includes(countryCode))
          ) {
            if (this.authCountries.includes('au'))
              this.setActiveCountry('au');
            else
              this.setActiveCountry(this.authCountries[0]);
          }
              
          // Promise Ended - Erase State
          this.authPromise = undefined;
          resolve(userInfo);
        } catch (error) {
          // Promise Ended - Erase State
          this.authPromise = undefined;
  
          reject(error)
        }
      });

      // Allocating promise to state, so it can be checked above, to prevent duplicate calls
      this.authPromise = promise;

      return promise;
    }
  }

  getActiveCountry(): string|undefined {
    return this.activeCountryCode;
  }
  setActiveCountry(newCountryCode: string) {
    this.activeCountryCode = newCountryCode;
    SetCrossSubdomainCookie('asCountryCode', newCountryCode, 14);
    APIHelper.AuthAPIs.forEach((itApi) => {
        itApi.setHeaderCommons('X-as-country-code', newCountryCode);
    });
  }
  isActiveCountryOutOfSync(): boolean {
    return (this.activeCountryCode !== GetActiveCountryCookie());
  }
}

export default new AuthUser();

export { AuthUser as AuthUserType };